import { JobStatus as JimJobStatus } from '@common-packages/shared-constants';
import { parseISO, isValid } from 'date-fns';

import { JobStatus as WsJobStatus, JobStatusString } from '../../shared/enums/job';

const parseJsonField = value => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'object') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null;
    }

    throw error;
  }
};

export const isUploadJobProcessing = status => {
  if (status === WsJobStatus.IN_PROGRESS) {
    return true;
  }

  if (typeof status === 'string') {
    const normalizedStatus = status.toUpperCase();

    return (
      normalizedStatus === JobStatusString.IN_PROGRESS ||
      status === JimJobStatus.WAITING ||
      status === JimJobStatus.PROCESSING
    );
  }

  return false;
};

export const isUploadJobSuccess = status => {
  if (status === WsJobStatus.SUCCESS) {
    return true;
  }

  if (typeof status === 'string') {
    const normalizedStatus = status.toUpperCase();

    return normalizedStatus === JobStatusString.SUCCESS || status === JimJobStatus.SUCCESS;
  }

  return false;
};

export const isUploadJobFailed = status => {
  if (status === WsJobStatus.FAILED) {
    return true;
  }

  if (typeof status === 'string') {
    const normalizedStatus = status.toUpperCase();

    return normalizedStatus === JobStatusString.FAILED || status === JimJobStatus.FAILED;
  }

  return false;
};

const hasExplicitTimezone = value => /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(value.trim());

const ISO_DATE_TIME_CAPTURE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?/;

const AMERICAN_DATE_TIME_CAPTURE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?/;

const padTwoDigits = value => String(value).padStart(2, '0');

const parseIsoUploadJobUtcDate = value => {
  const isoDateTimeMatch = value.match(ISO_DATE_TIME_CAPTURE_PATTERN);

  if (!isoDateTimeMatch) {
    return null;
  }

  const [, year, month, day, hour, minute, second] = isoDateTimeMatch;

  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ),
  );
};

// Timestamps arrive as UTC and are rendered in the viewer's own timezone, so omitting timeZone
// here is deliberate: Intl then falls back to the browser's zone.
const formatParsedUploadJobDate = date => {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).formatToParts(date);

  const getPart = (type, fallback = '00') =>
    parts.find(part => part.type === type)?.value ?? fallback;

  const dayPeriod = getPart('dayPeriod', '').toUpperCase();
  // en-US resolves a 12-hour clock to the "h" pattern, so ICU may hand back a single-digit hour
  // even though hour: '2-digit' was requested. Pad here to keep the MM/dd/yyyy hh:mm:ss shape.
  const hour = padTwoDigits(getPart('hour'));

  return `${padTwoDigits(getPart('month'))}/${padTwoDigits(getPart('day'))}/${getPart(
    'year',
  )} ${hour}:${getPart('minute')}:${getPart('second')}${dayPeriod ? ` ${dayPeriod}` : ''}`;
};

const parseAmericanUploadJobUtcDate = value => {
  const americanDateTimeMatch = value.match(AMERICAN_DATE_TIME_CAPTURE_PATTERN);

  if (!americanDateTimeMatch) {
    return null;
  }

  const [, month, day, year, hour, minute, second] = americanDateTimeMatch;

  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ),
  );
};

const parseUploadJobUtcDate = value => {
  if (value instanceof Date) {
    return parseUploadJobUtcDate(value.toISOString());
  }

  if (typeof value === 'number') {
    return new Date(value);
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const americanDate = parseAmericanUploadJobUtcDate(trimmed);

  if (americanDate && isValid(americanDate)) {
    return americanDate;
  }

  if (hasExplicitTimezone(trimmed)) {
    if (/(?:Z|[+-]00(?::?00)?)$/i.test(trimmed)) {
      const isoString = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
      const parsedIsoDate = parseISO(isoString);

      return isValid(parsedIsoDate) ? parsedIsoDate : null;
    }

    const utcFromComponents = parseIsoUploadJobUtcDate(trimmed);

    return utcFromComponents && isValid(utcFromComponents) ? utcFromComponents : null;
  }

  const isoDate = parseIsoUploadJobUtcDate(trimmed);

  if (isoDate && isValid(isoDate)) {
    return isoDate;
  }

  return null;
};

const normalizeUtcIsoString = value => {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return trimmed;
  }

  const americanDateTimeMatch = trimmed.match(AMERICAN_DATE_TIME_CAPTURE_PATTERN);

  if (americanDateTimeMatch) {
    const [, month, day, year, hour, minute, second] = americanDateTimeMatch;

    return `${year}-${padTwoDigits(month)}-${padTwoDigits(day)}T${hour}:${minute}:${second}Z`;
  }

  if (hasExplicitTimezone(trimmed)) {
    if (/(?:Z|[+-]00(?::?00)?)$/i.test(trimmed)) {
      return trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    }

    const utcFromComponents = parseIsoUploadJobUtcDate(trimmed);

    return utcFromComponents && isValid(utcFromComponents)
      ? utcFromComponents.toISOString()
      : trimmed;
  }

  const normalized = trimmed.replace(' ', 'T');

  return normalized.endsWith('Z') ? normalized : `${normalized}Z`;
};

const getUploadJobTimestamp = value => {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'number') {
    return new Date(value).toISOString();
  }

  return normalizeUtcIsoString(value);
};

export const getUploadJobCreatedOn = job =>
  getUploadJobTimestamp(job?.createdOn ?? job?.startTime ?? null);

export const getUploadJobFinishedOn = job =>
  getUploadJobTimestamp(job?.finishedOn ?? job?.endTime ?? null);

export const formatUploadJobDateTime = value => {
  const parsedDate = parseUploadJobUtcDate(value);

  if (!parsedDate || !isValid(parsedDate)) {
    return '';
  }

  return formatParsedUploadJobDate(parsedDate);
};

export const formatUploadJobCreatedOn = job => {
  if (!job) {
    return '';
  }

  return formatUploadJobDateTime(job.createdOn ?? job.startTime);
};

export const formatUploadJobCreatedOnForDisplay = value => formatUploadJobDateTime(value);

export const formatUploadJobFinishedOn = job => {
  if (!job) {
    return '';
  }

  return (
    formatUploadJobDateTime(job.finishedOn ?? job.endTime) ||
    formatUploadJobDateTime(getUploadJobFinishedOn(job))
  );
};

// SLT_2477724 LOCKED: "Importing data completed on" uses the browser's local time at success,
// formatted as MM/dd/yyyy hh:mm:ss AM/PM.
// Do not switch back to server/WebSocket UTC strings without explicit product approval.
export const formatUploadJobFinishedOnForDisplay = () => formatParsedUploadJobDate(new Date());

const getHdrIdFromParsedResult = parsedResult => {
  if (parsedResult == null) {
    return null;
  }

  if (typeof parsedResult === 'number' || typeof parsedResult === 'string') {
    return parsedResult;
  }

  return parsedResult.hdrId ?? parsedResult.HdrId ?? parsedResult.HDR_ID ?? null;
};

export const getHdrIdFromUploadJobResult = job => {
  if (!job) {
    return null;
  }

  const parsedResult = parseJsonField(job.result ?? job.returnedValue);

  return getHdrIdFromParsedResult(parsedResult);
};

export const getHdrIdFromUploadJob = job => {
  if (!job) {
    return null;
  }

  const hdrIdFromResult = getHdrIdFromUploadJobResult(job);

  if (hdrIdFromResult != null && hdrIdFromResult !== '') {
    return hdrIdFromResult;
  }

  return job.objectId ?? null;
};

// Batch id the job itself reported. Its presence means the import already wrote its outcome.
export const getExplicitBatchIdFromUploadJob = job => {
  if (!job) {
    return null;
  }

  const parsedError = parseJsonField(job.error ?? job.returnedError);
  const batchIdFromError = parsedError?.batchId ?? parsedError?.BatchId ?? null;

  if (batchIdFromError != null && batchIdFromError !== '') {
    return batchIdFromError;
  }

  const parsedResult = parseJsonField(job.result ?? job.returnedValue);
  const batchIdFromResult = parsedResult?.batchId ?? parsedResult?.BatchId ?? null;

  if (batchIdFromResult != null && batchIdFromResult !== '') {
    return batchIdFromResult;
  }

  return null;
};

// The async job row id is only a *candidate* batch id. Sending it as batchId makes the server
// treat it as confirmed and skip the candidate probing that verifies rejected rows actually exist
// under it, so it travels as its own request parameter instead.
export const getUniqueJobIdFromUploadJob = job => {
  if (job?.uniqueJobId == null || job.uniqueJobId === '') {
    return null;
  }

  return String(job.uniqueJobId);
};

// uniqueJobId is deliberately excluded: it is known from the first poll and so proves nothing
// about the import having finished. This predicate gates polling and the completion toast.
export const hasUploadJobImportReference = job =>
  Boolean(getHdrIdFromUploadJobResult(job) || getExplicitBatchIdFromUploadJob(job));

const isIsoLikeTimestamp = value =>
  typeof value === 'string' && ISO_DATE_TIME_CAPTURE_PATTERN.test(value.trim());

const pickPreferredUploadJobTimestamp = (...values) => {
  const definedValues = values.filter(value => value != null && value !== '');

  if (!definedValues.length) {
    return null;
  }

  return definedValues.find(isIsoLikeTimestamp) ?? definedValues[0];
};

const pickEarliestUploadJobTimestamp = (...values) => {
  const parsedTimestamps = values
    .filter(value => value != null && value !== '')
    .map(value => ({
      raw: value,
      parsed: parseUploadJobUtcDate(value),
    }))
    .filter(({ parsed }) => parsed && isValid(parsed));

  if (!parsedTimestamps.length) {
    return pickPreferredUploadJobTimestamp(...values);
  }

  parsedTimestamps.sort((left, right) => left.parsed.getTime() - right.parsed.getTime());

  return parsedTimestamps[0].raw;
};

export const mergeUploadJobData = (...jobs) => {
  const definedJobs = jobs.filter(Boolean);

  if (!definedJobs.length) {
    return null;
  }

  const jobWithImportIds = definedJobs.find(
    job => getHdrIdFromUploadJobResult(job) || getExplicitBatchIdFromUploadJob(job),
  );
  const jobWithTerminalStatus = definedJobs.find(
    job => isUploadJobSuccess(job.status) || isUploadJobFailed(job.status),
  );
  const merged = {
    ...definedJobs[0],
    ...(jobWithTerminalStatus ?? definedJobs[definedJobs.length - 1]),
  };

  const resolvedCreatedOn = pickEarliestUploadJobTimestamp(
    definedJobs[0]?.createdOn,
    definedJobs[0]?.startTime,
    jobWithImportIds?.createdOn,
    jobWithImportIds?.startTime,
    ...definedJobs.slice(1).flatMap(job => [job.createdOn, job.startTime]),
  );
  const resolvedFinishedOn = pickPreferredUploadJobTimestamp(
    jobWithImportIds?.finishedOn,
    jobWithImportIds?.endTime,
    ...definedJobs.flatMap(job => [job.finishedOn, job.endTime]),
  );

  if (jobWithImportIds) {
    merged.result = jobWithImportIds.result ?? merged.result;
    merged.returnedValue = jobWithImportIds.returnedValue ?? merged.returnedValue;
    merged.error = jobWithImportIds.error ?? merged.error;
    merged.returnedError = jobWithImportIds.returnedError ?? merged.returnedError;
    merged.objectId = jobWithImportIds.objectId ?? merged.objectId;
  }

  // Only the polled job-status payload carries uniqueJobId, so keep it even when a WebSocket
  // update without that field wins the merge.
  merged.uniqueJobId =
    definedJobs.find(job => job.uniqueJobId != null && job.uniqueJobId !== '')?.uniqueJobId ??
    merged.uniqueJobId ??
    null;

  merged.createdOn = getUploadJobTimestamp(resolvedCreatedOn);
  merged.startTime = getUploadJobTimestamp(resolvedCreatedOn);
  merged.finishedOn = getUploadJobTimestamp(resolvedFinishedOn);
  merged.endTime = getUploadJobTimestamp(resolvedFinishedOn);

  return merged;
};
